import { gql } from "@apollo/client";

import { AUTOMATION_FIELDS, FINANCE_PACKAGE_FIELDS } from "~/api/_queries";

import {
  AiAssistant,
  AiAssistantChat,
  CallInfo,
  Customer,
  CustomizeField,
  InventoryPart,
  Oem,
  PreventiveMaintenance,
  ProcedureInstance,
  ProcedureTemplate,
  Report,
  Resource,
  Team,
  Ticket,
  TimeTracker,
  User,
  WorkOrderType,
  Schedule,
  MachineHistory,
  TicketScheduleDraft,
  AiNote,
  AssetTemplate,
  Asset,
  Document,
} from "~/api/_fragments";
import {
  ACCEPT_CONNECTION_REQUEST,
  DECLINE_CONNECTION_REQUEST,
} from "~/services/connectionRequest/constants";
import {
  CREATE_CONTACT,
  DELETE_CONTACT,
  INVITE_CONTACTS,
  REMOVE_CONTACT_INVITE,
  RESEND_CONTACT_INVITE,
  UPDATE_CONTACT,
} from "~/services/contact/constants";
import {
  CREATE_CUSTOMER_PORTAL,
  DELETE_CUSTOMER_PORTAL,
  UPDATE_CUSTOMER_PORTAL,
} from "~/services/customerPortal/constants";
import {
  APPROVE_OCR_DOCUMENTS,
  CREATE_DOCUMENTS,
  DELETE_DOCUMENTS,
  INDEX_DOCUMENTS,
  SCAN_DOCUMENTS,
  UPDATE_DOCUMENT,
  UPLOAD_SEARCH_FEEDBACK,
} from "~/services/document/constants";
import { CREATE_REQUEST } from "~/services/request/constants";

export const ADD_AI_ASSISTANT_DOCUMENTS = "addAiAssistantDocuments";
export const CREATE_AI_ASSISTANT = "createAiAssistant";
export const REMVOVE_AI_ASSISTANT = "removeAiAssistant";
export const REMOVE_AI_ASSISTANT_DOCUMENTS = "removeAiAssistantDocuments";
export const RENAME_AI_ASSISTANT = "renameAiAssistant";
export const SUBMIT_IDEA_SUGGESTION = "submitIdeaSuggestion";
export const TRANSCRIBE_AI_VOICE_QUERY = "transcribeAiVoiceQuery";
export const UPDATE_OEM_AI_ASSISTANT_CONFIGURATION =
  "updateOemAiAssistantConfiguration";

// REMOTE
export const LOGIN_MUTATION = gql`
  mutation loginOemDashboard($userCredentials: userOemCredentials!) {
    loginOemDashboard(input: $userCredentials)
  }
`;

export const SIGNUP_MUTATION = gql`
  mutation signUpMutation($input: InputSignUp!) {
    signUp(input: $input)
  }
`;

export const LOGIN_CUSTOMER_PORTAL = gql`
  mutation loginCustomerPortal($userCredentials: userOemCredentials!) {
    loginCustomerPortal(input: $userCredentials) {
      token
      refreshToken
      redirectUrl
      message
      isError
    }
  }
`;

export const FORGOT_PASSWORD_MUTATION = gql`
  mutation forgotPassword($email: String!) {
    forgotPassword(email: $email)
  }
`;

export const VERIFY_PASSWORD_RESET_TOKEN_MUTATION = gql`
  mutation verifyPasswordResetToken($uid: String!, $token: String!) {
    verifyPasswordResetToken(uid: $uid, token: $token)
  }
`;

export const RESET_PASSWORD_MUTATION = gql`
  mutation resetPassword($uid: String!, $token: String!, $password: String!) {
    resetPassword(uid: $uid, token: $token, password: $password)
  }
`;

export const CREATE_OEM_SUPPORT_ACCOUNT_MUTATION = gql`
  mutation createOemSupportAccount($input: InputCreateOemSupportAccount!) {
    createOemSupportAccount(input: $input) {
      ...UserBasicData
    }
  }
  ${User.fragments.UserBasicData}
`;

export const INVITE_OEM_SUPPORT_ACCOUNT_MUTATION = gql`
  mutation inviteOemSupportAccount($input: InputInviteOemSupportAccount!) {
    inviteOemSupportAccount(input: $input) {
      ...UserBasicData
      status
    }
  }
  ${User.fragments.UserBasicData}
`;

export const CUSTOMER_PORTAL_SIGNUP_MUTATION = gql`
  mutation customerPortalSignup($input: InputCustomerPortalSignup!) {
    customerPortalSignup(input: $input)
  }
`;

export const UPDATE_OEM_SUPPORT_ACCOUNT_MUTATION = gql`
  mutation updateOemSupportAccount($input: InputUpdateOemSupportAccount!) {
    updateOemSupportAccount(input: $input) {
      ...UserBasicData
    }
  }
  ${User.fragments.UserBasicData}
`;

export const DELETE_OEM_SUPPORT_ACCOUNT_MUTATION = gql`
  mutation deleteOemSupportAccount($id: ID!) {
    deleteOemSupportAccount(id: $id)
  }
`;

export const RAISE_OEM_SUPPORT_QUERY_MUTATION = gql`
  mutation raiseOemSupportQuery(
    $companySize: String
    $contact: String
    $query: String
    $isDelete: Boolean
    $productType: String
  ) {
    raiseOemSupportQuery(
      companySize: $companySize
      contact: $contact
      query: $query
      isDelete: $isDelete
      productType: $productType
    )
  }
`;

export const CREATE_OEM_CUSTOMER_MUTATION = gql`
  mutation createOemCustomer($input: InputCreateOemCustomer!) {
    createOemCustomer(input: $input) {
      ...CustomerBasicData
    }
  }
  ${Customer.fragments.CustomerBasicData}
`;

export const CREATE_OEM_CUSTOMER_V2_MUTATION = gql`
  mutation createOemCustomerV2($input: InputCreateOemCustomerV2!) {
    createOemCustomerV2(input: $input) {
      ...CustomerFullData
    }
  }
  ${Customer.fragments.CustomerFullData}
`;

export const UPDATE_OEM_CUSTOMER_MUTATION = gql`
  mutation updateOemCustomer($input: InputUpdateOemCustomer!) {
    updateOemCustomer(input: $input) {
      ...CustomerFullData
    }
  }
  ${Customer.fragments.CustomerFullData}
`;

export const DELETE_OEM_CUSTOMER_MUTATION = gql`
  mutation deleteOemCustomer($customerId: ID!) {
    deleteOemCustomer(customerId: $customerId)
  }
`;

export const ASSIGN_COMPANIES_TO_PARENT_MUTATION = gql`
  mutation assignCompaniesToParent($input: InputAssignCompaniesToParent!) {
    assignCompaniesToParent(input: $input) {
      ...CustomerBasicData
      totalChildrenCount
      childrenCount
    }
  }
  ${Customer.fragments.CustomerBasicData}
`;

export const ASSIGN_PARENTS_TO_COMPANY_MUTATION = gql`
  mutation assignParentsToCompany($input: InputAssignParentsToCompany!) {
    assignParentsToCompany(input: $input) {
      ...CustomerBasicData
      totalChildrenCount
      childrenCount
    }
  }
  ${Customer.fragments.CustomerBasicData}
`;

export const UNASSIGN_COMPANY_FROM_PARENT_MUTATION = gql`
  mutation unassignCompanyFromParent($input: InputUnassignCompanyFromParent!) {
    unassignCompanyFromParent(input: $input) {
      ...CustomerBasicData
      totalChildrenCount
      childrenCount
    }
  }
  ${Customer.fragments.CustomerBasicData}
`;

export const UPDATE_OWN_OEM_MUTATION = gql`
  mutation updateOwnOem($input: InputUpdateOwnOem!) {
    updateOwnOem(input: $input) {
      ...OemBasicData
    }
  }
  ${Oem.fragments.OemBasicData}
`;

export const UPDATE_ARCHIVE_SETTINGS_MUTATION = gql`
  mutation updateArchiveSettings($input: InputUpdateArchiveSettings!) {
    updateArchiveSettings(input: $input) {
      _id
      archiveSettings {
        autoArchiveDelayDays
      }
    }
  }
`;

export const UPDATE_OEM_AI_ASSISTANT_CONFIGURATION_MUTATION = gql`
  mutation ${UPDATE_OEM_AI_ASSISTANT_CONFIGURATION}($input: UpdateOemAiAssistantConfiguration!) {
    ${UPDATE_OEM_AI_ASSISTANT_CONFIGURATION}(input: $input) {
      autoApproveOCR
    }
  }
`;

export const CREATE_OEM_MUTATION = gql`
  mutation createOem($input: InputCreateOem!) {
    createOem(input: $input) {
      _id
    }
  }
`;

export const UPDATE_OWN_OEM_TICKET_MUTATION = gql`
  mutation updateOwnOemTicket($input: InputUpdateOwnOemTicket!) {
    updateOwnOemTicket(input: $input) {
      ...TicketSingleData
    }
  }
  ${Ticket.fragments.TicketSingleData}
`;

export const UPDATE_OWN_OEM_TICKET_TYPE_MUTATION = gql`
  mutation updateOwnOemTicketType($input: InputUpdateOwnOemTicketType!) {
    updateOwnOemTicketType(input: $input) {
      ...TicketSingleData
    }
  }
  ${Ticket.fragments.TicketSingleData}
`;

export const ADD_TICKET_RESOURCE_MUTATION = gql`
  mutation addTicketResource($input: InputAddTicketResource!) {
    addTicketResource(input: $input) {
      ...TicketSingleData
    }
  }
  ${Ticket.fragments.TicketSingleData}
`;

export const REMOVE_TICKET_RESOURCES_MUTATION = gql`
  mutation removeTicketResources($input: InputRemoveTicketResources!) {
    removeTicketResources(input: $input) {
      ...TicketSingleData
    }
  }
  ${Ticket.fragments.TicketSingleData}
`;

export const UPDATE_TICKET_KANBAN_OWN_OEM_TICKET_MUTATION = gql`
  mutation updateOwnOemKanbanBoardTicketCard(
    $input: InputUpdateOwnOemKanbanBoardTicketCard!
  ) {
    updateOwnOemKanbanBoardTicketCard(input: $input)
  }
`;

export const CREATE_OWN_OEM_TICKET_MUTATION = gql`
  mutation createOwnOemTicket($input: InputCreateOwnOemTicket!) {
    createOwnOemTicket(input: $input) {
      ...TicketSingleData
    }
  }
  ${Ticket.fragments.TicketSingleData}
`;

export const JOIN_CALL_FOR_TICKET_FROM_FACILITY_MUTATION = gql`
  mutation joinCallForTicketFromFacility($channel: String!) {
    joinCallForTicketFromFacility(channel: $channel) {
      ...CallInfoData
    }
  }
  ${CallInfo.fragments.CallInfoData}
`;

export const UPDATE_PERSONAL_SETTINGS = gql`
  mutation updatePersonalSettings(
    $personalSettings: InputUpdatePersonalSettings
    $emailNotificationSettings: InputEmailNotificationSettings
  ) {
    updatePersonalSettings(
      personalSettings: $personalSettings
      emailNotificationSettings: $emailNotificationSettings
    ) {
      ...UserBasicData
    }
  }
  ${User.fragments.UserBasicData}
`;

export const ACTIVE_INACTIVE_OWN_OEM_ADDITIONAL_FIELD = gql`
  mutation activeInactiveOwnOemAdditionalField($id: ID!, $enabled: Boolean!) {
    activeInactiveOwnOemAdditionalField(_id: $id, enabled: $enabled) {
      ...CustomAdditionFieldData
    }
  }
  ${CustomizeField.fragments.CustomAdditionFieldData}
`;

export const CREATE_OWN_OEM_CUSTOM_ADDITION_FIELD = gql`
  mutation createOwnOemCustomField($input: InputInsertCustomAdditionalField) {
    createOwnOemCustomField(input: $input) {
      ...CustomAdditionFieldData
    }
  }
  ${CustomizeField.fragments.CustomAdditionFieldData}
`;
export const UPDATE_OWN_OEM_CUSTOM_ADDITION_FIELD = gql`
  mutation updateOwnOemCustomField($input: InputUpdateCustomAdditionalField) {
    updateOwnOemCustomField(input: $input) {
      ...CustomAdditionFieldData
    }
  }
  ${CustomizeField.fragments.CustomAdditionFieldData}
`;
export const UPDATE_OWN_OEM_CUSTOM_FIELDS_ORDER = gql`
  mutation updateOwnOemCustomFieldsOrder($input: InputUpdateCustomFieldsOrder) {
    updateOwnOemCustomFieldsOrder(input: $input)
  }
`;

export const DELETE_OWN_OEM_CUSTOM_ADDITIONAL_FIELD = gql`
  mutation deleteOwnOemCustomAdditionalField(
    $input: InputDeleteOwnOemCustomAdditionalField
  ) {
    deleteOwnOemCustomAdditionalField(input: $input)
  }
`;

export const CREATE_PREVENTIVE_MAINTENANCE_EVENT = gql`
  mutation createPreventiveMaintenanceEvent(
    $input: InputCreatePreventiveMaintenanceEvent!
  ) {
    createPreventiveMaintenanceEvent(input: $input) {
      ...PreventiveEventData
    }
  }
  ${PreventiveMaintenance.fragments.PreventiveEventData}
`;

export const UPDATE_PREVENTIVE_MAINTENANCE_EVENT = gql`
  mutation updatePreventiveMaintenanceEvent(
    $input: InputUpdatePreventiveMaintenanceEvent!
  ) {
    updatePreventiveMaintenanceEvent(input: $input) {
      ...PreventiveEventData
    }
  }
  ${PreventiveMaintenance.fragments.PreventiveEventData}
`;

export const DELETE_PREVENTIVE_MAINTENANCE_EVENT = gql`
  mutation deletePreventiveMaintenanceEvent($id: ID!) {
    deletePreventiveMaintenanceEvent(id: $id) {
      _id
      oem {
        _id
      }
    }
  }
`;

export const CREATE_PREVENTIVE_MAINTENANCE_WORK_ORDER = gql`
  mutation CreatePMEWorkOrder(
    $input: InputCreatePreventiveMaintenanceWorkOrder!
  ) {
    createPreventiveMaintenanceWorkOrder(input: $input) {
      alreadyExisted
      workOrder {
        _id
        ticketId
        isDeleted
      }
      occurrence {
        triggerDate
        dueDate
        scheduledStart
        scheduledEnd
        canCreateWorkOrder
        workOrderGenerationPending
        workOrder {
          _id
          ticketId
          isDeleted
        }
      }
    }
  }
`;

export const NOTIFY_ON_INTERNAL_POST_MUTATION = gql`
  mutation notifyOnInternalNotePost($input: InputNotifyOnInternalNotePost!) {
    notifyOnInternalNotePost(input: $input)
  }
`;

export const CREATE_OWN_OEM_INVENTORY_PART_MUTATION = gql`
  mutation createOwnOemInventoryPart($input: InputCreateInventoryPart!) {
    createOwnOemInventoryPart(input: $input) {
      ...InventoryPartFullData
    }
  }
  ${InventoryPart.fragments.InventoryPartFullData}
`;

export const UPDATE_OWN_OEM_INVENTORY_PART_MUTATION = gql`
  mutation updateOwnOemInventoryPart($input: InputUpdateInventoryPart!) {
    updateOwnOemInventoryPart(input: $input) {
      ...InventoryPartFullData
    }
  }
  ${InventoryPart.fragments.InventoryPartFullData}
`;

export const DELETE_OWN_OEM_INVENTORY_PART_MUTATION = gql`
  mutation deleteOwnOemInventoryPart($id: ID!) {
    deleteOwnOemInventoryPart(id: $id) {
      _id
    }
  }
`;

export const ADD_STOCK_TO_PART_MUTATION = gql`
  mutation AddStockToPart($input: InputAddStockToPart!) {
    addStockToPart(input: $input) {
      _id
      name
      articleNumber
      totalStock
      availableStock
      totalCost
    }
  }
`;

export const UPDATE_STOCK_OPERATION_MUTATION = gql`
  mutation UpdateStockOperation($input: InputUpdateStockOperation!) {
    updateStockOperation(input: $input) {
      _id
      operationType
      quantity
      costPerPart
      comment
      updatedAt
    }
  }
`;

export const REMOVE_STOCK_OPERATION_MUTATION = gql`
  mutation RemoveStockOperation($operationId: ID!) {
    removeStockOperation(operationId: $operationId)
  }
`;

export const ALLOCATE_PENDING_STOCK_MUTATION = gql`
  mutation AllocatePendingStock($operationId: ID!) {
    allocatePendingStock(operationId: $operationId) {
      _id
      allocationStatus
      operationType
      quantity
      inventoryPart {
        _id
        name
        availableStock
      }
    }
  }
`;

export const ADD_OWN_OEM_PART_TO_TICKET_MUTATION = gql`
  mutation assignOwnOemInventoryPartsToTicket(
    $input: InputAssignInventoryPartsToTicket!
  ) {
    assignOwnOemInventoryPartsToTicket(input: $input) {
      ...TicketSingleData
    }
  }
  ${Ticket.fragments.TicketSingleData}
`;

export const REMOVE_OWN_OEM_PART_FROM_TICKET_MUTATION = gql`
  mutation removeOwnOemInventoryPartFromTicket(
    $input: InputRemoveInventoryPartFromTicket!
  ) {
    removeOwnOemInventoryPartFromTicket(input: $input) {
      ...TicketSingleData
    }
  }
  ${Ticket.fragments.TicketSingleData}
`;

export const EDIT_OWN_OEM_PART_ON_TICKET_MUTATION = gql`
  mutation editOwnOemInventoryPartOnTicket(
    $input: InputEditInventoryPartOnTicket!
  ) {
    editOwnOemInventoryPartOnTicket(input: $input) {
      ...TicketSingleData
    }
  }
  ${Ticket.fragments.TicketSingleData}
`;

export const CREATE_TIME_TRACKER_TAG_MUTATION = gql`
  mutation createOwnOemTimeTrackerTag($input: InputInsertTag!) {
    createOwnOemTimeTrackerTag(input: $input) {
      ...TimeTrackerBasicData
    }
  }
  ${TimeTracker.fragments.TimeTrackerBasicData}
`;

export const UPDATE_TIME_TRACKER_TAG_MUTATION = gql`
  mutation updateOwnOemTimeTrackerTag($input: InputUpdateTag!) {
    updateOwnOemTimeTrackerTag(input: $input) {
      ...TimeTrackerBasicData
    }
  }
  ${TimeTracker.fragments.TimeTrackerBasicData}
`;

export const DELETE_TIME_TRACKER_TAG_MUTATION = gql`
  mutation deleteOwnOemTimeTrackerTag($id: ID!) {
    deleteOwnOemTimeTrackerTag(id: $id) {
      ...TimeTrackerBasicData
    }
  }
  ${TimeTracker.fragments.TimeTrackerBasicData}
`;

export const ADD_TICKET_TIME_TRACKER_LOG_MUTATION = gql`
  mutation addTimeTrackerLog($input: InputAddTimeTrackerLog!) {
    addTimeTrackerLog(input: $input) {
      ...TicketSingleData
    }
  }
  ${Ticket.fragments.TicketSingleData}
`;

export const UPDATE_TICKET_TIME_TRACKER_LOG_MUTATION = gql`
  mutation updateTimeTrackerLog($input: InputUpdateTimeTrackerLog!) {
    updateTimeTrackerLog(input: $input) {
      ...TicketSingleData
    }
  }
  ${Ticket.fragments.TicketSingleData}
`;

export const REMOVE_TIME_TRACKER_LOG_FROM_TICKET_MUTATION = gql`
  mutation removeTimeTrackerLogFromTicket($input: InputRemoveTimeTrackerLog!) {
    removeTimeTrackerLogFromTicket(input: $input) {
      ...TicketSingleData
    }
  }
  ${Ticket.fragments.TicketSingleData}
`;

export const UPDATE_TITLE_TICKET_MUTATION = gql`
  mutation updateTitleTicket($input: InputUpdateTitleTicket!) {
    updateTitleTicket(input: $input) {
      ...TicketSingleData
    }
  }
  ${Ticket.fragments.TicketSingleData}
`;

export const UPDATE_DESCRIPTION_TICKET_MUTATION = gql`
  mutation updateDescriptionTicket($input: InputUpdateDescriptionTicket!) {
    updateDescriptionTicket(input: $input) {
      ...TicketSingleData
    }
  }
  ${Ticket.fragments.TicketSingleData}
`;

export const UPDATE_SOLUTION_TICKET_MUTATION = gql`
  mutation updateSolutionTicket($input: InputUpdateSolutionTicket!) {
    updateSolutionTicket(input: $input) {
      ...TicketSingleData
    }
  }
  ${Ticket.fragments.TicketSingleData}
`;

export const HANDLE_IMPORT_UPLOAD_ID_MUTATION = gql`
  mutation handleImportUploadId($input: InputHandleImportUploadId!) {
    handleImportUploadId(input: $input)
  }
`;

export const SAVE_OWN_OEM_PROCEDURE_TEMPLATE = gql`
  mutation SaveOwnOemProcedureTemplate($input: InputSaveProcedureTemplate!) {
    saveOwnOemProcedureTemplate(input: $input) {
      ...ProcedureTemplateFullData
    }
  }
  ${ProcedureTemplate.fragments.ProcedureTemplateFullData}
`;

export const UPDATE_PROCEDURE_HEADER_FIELD = gql`
  mutation UpdateProcedureHeaderField(
    $input: InputUpdateProcedureHeaderField!
  ) {
    updateProcedureHeaderField(input: $input) {
      ...ProcedureTemplateFullData
    }
  }
  ${ProcedureTemplate.fragments.ProcedureTemplateFullData}
`;

export const ADD_PROCEDURE_HEADER_CUSTOM_FIELDS = gql`
  mutation AddProcedureHeaderCustomFields(
    $input: InputAddProcedureHeaderCustomFields!
  ) {
    addProcedureHeaderCustomFields(input: $input) {
      ...ProcedureTemplateFullData
    }
  }
  ${ProcedureTemplate.fragments.ProcedureTemplateFullData}
`;

export const REMOVE_PROCEDURE_HEADER_CUSTOM_FIELD = gql`
  mutation RemoveProcedureHeaderCustomField(
    $input: InputRemoveProcedureHeaderCustomField!
  ) {
    removeProcedureHeaderCustomField(input: $input) {
      ...ProcedureTemplateFullData
    }
  }
  ${ProcedureTemplate.fragments.ProcedureTemplateFullData}
`;

export const DELETE_OWN_OEM_PROCEDURE_TEMPLATE = gql`
  mutation deleteOwnOemProcedureTemplate($id: ID!) {
    deleteOwnOemProcedureTemplate(id: $id) {
      _id
    }
  }
`;
export const DUPLICATE_OWN_OEM_PROCEDURE_TEMPLATE = gql`
  mutation duplicateOwnOemProcedureTemplate($id: ID!) {
    duplicateOwnOemProcedureTemplate(id: $id)
  }
`;

export const ATTACH_OWN_OEM_PROCEDURE_TO_WORK_ORDER = gql`
  mutation attachOwnOemProcedureToWorkOrder(
    $input: InputAttachProcedureToWorkOrder!
  ) {
    attachOwnOemProcedureToWorkOrder(input: $input)
  }
`;

export const ATTACH_MULTIPLE_PROCEDURE_TEMPLATES_TO_WORK_ORDER = gql`
  mutation attachMultipleProcedureTemplatesToWorkOrder(
    $input: InputAttachMultipleProcedureTemplatesToWorkOrder!
  ) {
    attachMultipleProcedureTemplatesToWorkOrder(input: $input) {
      workOrderId
      procedureIds
    }
  }
`;

export const ATTACH_MULTIPLE_FILLED_PROCEDURES_TO_WORK_ORDER = gql`
  mutation attachMultipleFilledProceduresToWorkOrder(
    $input: InputAttachMultipleFilledProceduresToWorkOrder!
  ) {
    attachMultipleFilledProceduresToWorkOrder(input: $input) {
      workOrderId
      procedureIds
    }
  }
`;

export const ATTACH_DETACHED_PROCEDURE_TO_WORK_ORDER = gql`
  mutation attachDetachedProcedureToWorkOrder(
    $input: InputAttachDetachedProcedureToWorkOrder!
  ) {
    attachDetachedProcedureToWorkOrder(input: $input)
  }
`;

export const DETACH_OWN_OEM_PROCEDURE_TO_WORK_ORDER = gql`
  mutation detachOwnOemProcedureFromWorkOrder(
    $input: InputDetachProcedureFromWorkOrder!
  ) {
    detachOwnOemProcedureFromWorkOrder(input: $input) {
      _id
    }
  }
`;

export const SAVE_OWN_OEM_PROCEDURE_INSTANCE = gql`
  mutation saveOwnOemProcedure($input: InputSaveProcedure!) {
    saveOwnOemProcedure(input: $input) {
      ...ProcedureInstanceFullData
    }
  }
  ${ProcedureInstance.fragments.ProcedureInstanceFullData}
`;

export const RESTORE_OWN_OEM_PROCEDURE_FROM_ACTIVITY_LOG = gql`
  mutation restoreOwnOemProcedureFromActivityLog(
    $input: InputRestoreProcedureFromActivityLog!
  ) {
    restoreOwnOemProcedureFromActivityLog(input: $input) {
      procedure {
        ...ProcedureInstanceFullData
      }
      unmatchedFields
    }
  }
  ${ProcedureInstance.fragments.ProcedureInstanceFullData}
`;

export const ATTACH_PROCEDURE_TO_PM = gql`
  mutation attachProcedureToPreventiveMaintenance(
    $input: InputAttachProcedureToPM!
  ) {
    attachProcedureToPreventiveMaintenance(input: $input)
  }
`;

export const ATTACH_PROCEDURE_TO_TEMPLATE_MUTATION = gql`
  mutation attachProceduresToTemplate($procedures: [ID], $templateId: ID!) {
    attachProceduresToTemplate(
      procedures: $procedures
      templateId: $templateId
    ) {
      ...AssetTemplateFullData
    }
  }
  ${AssetTemplate.fragments.AssetTemplateFullData}
`;

export const DETACH_PROCEDURE_FROM_TEMPLATE_MUTATION = gql`
  mutation detachProcedureFromTemplate($procedureId: ID, $templateId: ID!) {
    detachProcedureFromTemplate(
      procedureId: $procedureId
      templateId: $templateId
    ) {
      ...AssetTemplateFullData
    }
  }
  ${AssetTemplate.fragments.AssetTemplateFullData}
`;

export const FINALIZE_OWN_OEM_PROCEDURE_INSTANCE = gql`
  mutation finalizeOwnOemProcedure($input: InputFinalizeProcedure!) {
    finalizeOwnOemProcedure(input: $input)
  }
`;

export const CREATE_DETACHED_PROCEDURE = gql`
  mutation createDetachedProcedure($input: InputCreateDetachedProcedure!) {
    createDetachedProcedure(input: $input)
  }
`;

export const DELETE_DETACHED_PROCEDURE = gql`
  mutation deleteDetachedProcedure($id: ID!) {
    deleteDetachedProcedure(id: $id) {
      _id
    }
  }
`;

export const ADD_USER_BOARD = gql`
  mutation addUserBoard($board: InputUserBoard!) {
    addUserBoard(board: $board) {
      ...UserAuthData
    }
  }
  ${User.fragments.UserAuthData}
`;

export const REMOVE_USER_BOARD = gql`
  mutation removeUserBoard($board: InputUserBoard!) {
    removeUserBoard(board: $board) {
      ...UserAuthData
    }
  }
  ${User.fragments.UserAuthData}
`;

export const LINK_EMAIL_ACCOUNT_MUTATION = gql`
  mutation linkEmailAccount($code: String!, $scope: String!) {
    linkEmailAccount(code: $code, scope: $scope) {
      accounts {
        _id
        emailAddress
        emailAccountStatus
        needsReconnect
        isSynced
      }
      maximumAllowedEmailAccounts
    }
  }
`;

export const UNLINK_EMAIL_ACCOUNT_MUTATION = gql`
  mutation unlinkEmailAccount($emailAddressId: ID!, $scope: String!) {
    unlinkEmailAccount(emailAddressId: $emailAddressId, scope: $scope) {
      accounts {
        _id
        emailAddress
        emailAccountStatus
      }
      maximumAllowedEmailAccounts
    }
  }
`;

export const SEND_EMAIL_MUTATION = gql`
  mutation sendEmail($scope: String!, $input: InputSendEmail!) {
    sendEmail(scope: $scope, input: $input)
  }
`;

export const MOVE_EMAIL_TO_FOLDER_MUTATION = gql`
  mutation moveEmailToFolder(
    $scope: String!
    $messageIds: [String]!
    $folderId: String!
    $emailAddressId: ID!
  ) {
    moveEmailToFolder(
      scope: $scope
      messageIds: $messageIds
      folderId: $folderId
      emailAddressId: $emailAddressId
    )
  }
`;

export const LINK_TICKETS = gql`
  mutation linkTickets($ticketId: ID!, $linkedTicketId: ID!) {
    linkTickets(ticketId: $ticketId, linkedTicketId: $linkedTicketId) {
      _id
      linkedTickets {
        ticket {
          _id
          ticketId
          ticketType {
            _id
            name
            icon
            color
            isInternal
            isSystem
            descriptionConfig {
              show
              isRequired
              fieldName
            }
          }
          title
          status
          facility {
            name
          }
          machine {
            name
          }
        }
        linkedBy {
          _id
        }
        linkedAt
      }
    }
  }
`;

export const UNLINK_TICKETS = gql`
  mutation unlinkTickets($ticketId: ID!, $linkedTicketId: ID!) {
    unlinkTickets(ticketId: $ticketId, linkedTicketId: $linkedTicketId) {
      _id
      linkedTickets {
        ticket {
          _id
          ticketId
          ticketType {
            _id
            name
            icon
            color
            isInternal
            isSystem
            descriptionConfig {
              show
              isRequired
              fieldName
            }
          }
          title
          status
          facility {
            name
          }
          machine {
            name
          }
        }
        linkedBy {
          _id
        }
        linkedAt
      }
    }
  }
`;

export const ASSIGN_TEAM_TICKET_TYPES = gql`
  mutation assignTicketType($input: AssignTicketTypeInput!) {
    assignTicketType(input: $input) {
      oem {
        _id
      }
      name
      description
      createdAt
      _id
    }
  }
`;

export const CREATE_TEAM_MUTATION = gql`
  mutation createTeam($input: InputCreateTeam) {
    createTeam(input: $input) {
      oem {
        _id
      }
      name
      description
      createdAt
      _id
    }
  }
`;

export const DELETE_TEAM_MUTATION = gql`
  mutation deleteTeam($id: ID!) {
    deleteTeam(id: $id)
  }
`;

export const REMOVE_TICKET_TYPE_TEAM_MUTATION = gql`
  mutation removeTicketType($input: RemoveTicketTypeInput) {
    removeTicketType(input: $input) {
      oem {
        _id
      }
      name
      description
      createdAt
      _id
    }
  }
`;

export const UPDATE_TEAM_MUTATION = gql`
  mutation updateTeam($input: InputUpdateTeam!) {
    updateTeam(input: $input) {
      ...TeamData
    }
  }
  ${Team.fragments.TeamData}
`;

export const ASSIGN_MULTIPLE_MACHINES_TO_TEAM_MUTATION = gql`
  mutation assignMultipleMachinesToTeam(
    $input: InputAssignMultipleMachinesToTeam!
  ) {
    assignMultipleMachinesToTeam(input: $input) {
      _id
    }
  }
`;

export const ASSIGN_MULTIPLE_CUSTOMERS_TO_TEAM_MUTATION = gql`
  mutation assignMultipleCustomersToTeam(
    $input: InputAssignMultipleCustomersToTeam!
  ) {
    assignMultipleCustomersToTeam(input: $input) {
      _id
    }
  }
`;

export const ASSIGN_MULTIPLE_USERS_TO_TEAM_MUTATION = gql`
  mutation assignMultipleUsersToTeam($input: InputAssignMultipleUsersToTeam!) {
    assignMultipleUsersToTeam(input: $input) {
      _id
    }
  }
`;

export const ASSIGN_PROCEDURE_TEMPLATES_TO_TEAM_MUTATION = gql`
  mutation assignProcedureTemplatesToTeam(
    $input: InputAssignProcedureTemplatesToTeam!
  ) {
    assignProcedureTemplatesToTeam(input: $input)
  }
`;

export const UNASSIGN_PROCEDURE_TEMPLATE_FROM_TEAM_MUTATION = gql`
  mutation unassignProcedureTemplateFromTeam(
    $input: InputUnassignProcedureTemplateFromTeam!
  ) {
    unassignProcedureTemplateFromTeam(input: $input)
  }
`;

export const UNASSIGN_CUSTOMER_FROM_TEAM_MUTATION = gql`
  mutation unassignCustomerFromTeam($input: InputUnassignCustomerFromTeam!) {
    unassignCustomerFromTeam(input: $input) {
      _id
    }
  }
`;

export const UNASSIGN_MACHINE_FROM_TEAM_MUTATION = gql`
  mutation unassignMachineFromTeam($input: InputUnassignMachineFromTeam!) {
    unassignMachineFromTeam(input: $input) {
      _id
    }
  }
`;

export const UNASSIGN_USER_FROM_TEAM_MUTATION = gql`
  mutation unassignUserFromTeam($input: InputUnassignUserFromTeam!) {
    unassignUserFromTeam(input: $input) {
      _id
    }
  }
`;

export const CREATE_MULTIPART_UPLOAD_MUTATION = gql`
  mutation (
    $filename: String!
    $filetype: String!
    $type: String
    $forceCustomPath: Boolean
    $fileSize: Int!
    $partSize: Int!
    $forceUniqueFilename: Boolean
  ) {
    _createMultiPartUploadS3(
      filename: $filename
      filetype: $filetype
      type: $type
      forceCustomPath: $forceCustomPath
      fileSize: $fileSize
      partSize: $partSize
      forceUniqueFilename: $forceUniqueFilename
    ) {
      UploadId
      signedUrls
      fileKey
    }
  }
`;

export const COMPLETE_MULTIPART_UPLOAD_MUTATION = gql`
  mutation (
    $UploadId: String!
    $MultipartUpload: CompletedMultipartUpload
    $fileKey: String!
  ) {
    _completeMultiPartUploadS3(
      UploadId: $UploadId
      MultipartUpload: $MultipartUpload
      fileKey: $fileKey
    ) {
      url
    }
  }
`;

export const CREATE_OEM_TICKET_TYPE_MUTATION = gql`
  mutation createOemTicketType($input: InputTicketType!) {
    createOemTicketType(input: $input) {
      ...WorkOrderTypeData
    }
  }
  ${WorkOrderType.fragments.WorkOrderTypeData}
`;

export const UPDATE_OEM_TICKET_TYPE_MUTATION = gql`
  mutation updateOemTicketType($id: ID!, $input: InputUpdateOemTicketType!) {
    updateOemTicketType(id: $id, input: $input) {
      ...WorkOrderTypeData
    }
  }
  ${WorkOrderType.fragments.WorkOrderTypeData}
`;

export const DELETE_OEM_TICKET_TYPE_MUTATION = gql`
  mutation deleteOemTicketType($id: ID!, $input: InputDeleteOemTicketType) {
    deleteOemTicketType(id: $id, input: $input)
  }
`;

export const CONFIGURE_OEM_TICKET_TYPE_MUTATION = gql`
  mutation configureOemTicketType($id: ID!, $input: InputTicketTypeConfig!) {
    configureOemTicketType(id: $id, input: $input) {
      ...WorkOrderTypeData
    }
  }
  ${WorkOrderType.fragments.WorkOrderTypeData}
`;

// Support Ticket types — reuse the shared TicketType output (WorkOrderTypeData)
// and the shared inputs; only delete takes a distinct input.
export const CREATE_OEM_SUPPORT_TICKET_TYPE_MUTATION = gql`
  mutation createOemSupportTicketType($input: InputTicketType!) {
    createOemSupportTicketType(input: $input) {
      ...WorkOrderTypeData
    }
  }
  ${WorkOrderType.fragments.WorkOrderTypeData}
`;

export const UPDATE_OEM_SUPPORT_TICKET_TYPE_MUTATION = gql`
  mutation updateOemSupportTicketType(
    $id: ID!
    $input: InputUpdateOemTicketType!
  ) {
    updateOemSupportTicketType(id: $id, input: $input) {
      ...WorkOrderTypeData
    }
  }
  ${WorkOrderType.fragments.WorkOrderTypeData}
`;

export const DELETE_OEM_SUPPORT_TICKET_TYPE_MUTATION = gql`
  mutation deleteOemSupportTicketType(
    $id: ID!
    $input: InputDeleteOemSupportTicketType
  ) {
    deleteOemSupportTicketType(id: $id, input: $input)
  }
`;

export const CONFIGURE_OEM_SUPPORT_TICKET_TYPE_MUTATION = gql`
  mutation configureOemSupportTicketType(
    $id: ID!
    $input: InputTicketTypeConfig!
  ) {
    configureOemSupportTicketType(id: $id, input: $input) {
      ...WorkOrderTypeData
    }
  }
  ${WorkOrderType.fragments.WorkOrderTypeData}
`;

export const CREATE_OWN_OEM_REPORT_MUTATION = gql`
  mutation createOwnOemReport($input: InputCreateOwnOemReport!) {
    createOwnOemReport(input: $input) {
      _id
      title
      entity
    }
  }
`;

export const CREATE_OWN_OEM_DUPLICATE_REPORT_MUTATION = gql`
  mutation duplicateOwnOemReport($input: InputDuplicateOwnOemReport!) {
    duplicateOwnOemReport(input: $input) {
      _id
    }
  }
`;

export const DELETE_REPORT_MUTATION = gql`
  mutation deleteOwnOemReport($id: ID!) {
    deleteOwnOemReport(id: $id)
  }
`;

export const UPDATE_REPORT_MUTATION = gql`
  mutation updateOwnOemReport($input: InputUpdateOwnOemReport!) {
    updateOwnOemReport(input: $input) {
      ...InputReportData
    }
  }
  ${Report.fragment.InputReportData}
`;

export const ADD_TICKET_FOLLOWER_MUTATION = gql`
  mutation addTicketFollower($input: InputAddTicketFollower!) {
    addTicketFollower(input: $input) {
      _id
      followers {
        ...UserBasicData
      }
    }
  }
  ${User.fragments.UserBasicData}
`;

export const REMOVE_TICKET_FOLLOWER_MUTATION = gql`
  mutation removeTicketFollower($input: InputRemoveTicketFollower!) {
    removeTicketFollower(input: $input) {
      _id
      followers {
        ...UserBasicData
      }
    }
  }
  ${User.fragments.UserBasicData}
`;

export const ADD_TICKET_ASSIGNEE_MUTATION = gql`
  mutation addTicketAssignee($input: InputAddTicketAssignee!) {
    addTicketAssignee(input: $input) {
      _id
      assignees {
        ...UserBasicData
      }
    }
  }
  ${User.fragments.UserBasicData}
`;

export const ADD_TICKET_ASSIGNEES_MUTATION = gql`
  mutation addTicketAssignees($input: InputAddTicketAssignees!) {
    addTicketAssignees(input: $input) {
      _id
      assignees {
        ...UserBasicData
      }
    }
  }
  ${User.fragments.UserBasicData}
`;

export const ADD_TICKET_ATTACHMENT_MUTATION = gql`
  mutation addTicketAttachments($input: InputAddTicketAttachments!) {
    addTicketAttachments(input: $input) {
      _id
      attachments {
        name
        type
        url
        size
      }
    }
  }
`;

export const REMOVE_TICKET_ATTACHMENT_MUTATION = gql`
  mutation removeTicketAttachment($input: InputRemoveTicketAttachment!) {
    removeTicketAttachment(input: $input) {
      _id
      ticketId
    }
  }
`;

export const REMOVE_TICKET_ASSIGNEE_MUTATION = gql`
  mutation removeTicketAssignee($input: InputRemoveTicketAssignee!) {
    removeTicketAssignee(input: $input) {
      _id
      assignees {
        ...UserBasicData
      }
      ...TicketScheduleData
    }
  }
  ${User.fragments.UserBasicData}
  ${Schedule.fragments.TicketScheduleData}
`;

export const CREATE_MACHINE_HISTORY_NOTE_MUTATION = gql`
  mutation createMachineHistoryNote($input: InputCreateMachineHistoryNote) {
    createMachineHistoryNote(input: $input) {
      ...MachineHistoryData
    }
  }
  ${MachineHistory.fragments.MachineHistoryData}
`;

export const UPDATE_MACHINE_HISTORY_NOTE_MUTATION = gql`
  mutation updateMachineHistoryNote($input: InputUpdateMachineHistoryNote!) {
    updateMachineHistoryNote(input: $input) {
      ...MachineHistoryData
    }
  }
  ${MachineHistory.fragments.MachineHistoryData}
`;

export const DELETE_MACHINE_HISTORY_NOTE_MUTATION = gql`
  mutation deleteMachineHistoryNote($machine: ID!, $id: ID!) {
    deleteMachineHistoryNote(machine: $machine, id: $id)
  }
`;

export const DELETE_OWN_OEM_TICKET_MUTATION = gql`
  mutation deleteOwnOemTicket($input: InputDeleteOwnOemTicket!) {
    deleteOwnOemTicket(input: $input)
  }
`;

export const ARCHIVE_OWN_OEM_TICKET_MUTATION = gql`
  mutation archiveOwnOemTicket($input: InputArchiveOwnOemTicket!) {
    archiveOwnOemTicket(input: $input) {
      _id
      archived
      archivedAt
      archivedBy {
        _id
        name
      }
    }
  }
`;

export const UNARCHIVE_OWN_OEM_TICKET_MUTATION = gql`
  mutation unarchiveOwnOemTicket($input: InputUnarchiveOwnOemTicket!) {
    unarchiveOwnOemTicket(input: $input) {
      _id
      archived
      archivedAt
      archivedBy {
        _id
        name
      }
    }
  }
`;

export const ADD_AI_ASSISTANT_DOCUMENTS_MUTATION = gql`
  mutation ${ADD_AI_ASSISTANT_DOCUMENTS}($input: InputAddAiAssistantDocuments!) {
    ${ADD_AI_ASSISTANT_DOCUMENTS}(input: $input)
  }
`;

export const CREATE_AI_ASSISTANT_MUTATION = gql`
  mutation ${CREATE_AI_ASSISTANT}($input: InputCreateAiAssistant) {
    ${CREATE_AI_ASSISTANT}(input: $input) {
      ...BasicData
    }
  }
  ${AiAssistant.BasicData}
`;

export const REMVOVE_AI_ASSISTANT_MUTATION = gql`
  mutation ${REMVOVE_AI_ASSISTANT}($aiAssistantID: ID!) {
    ${REMVOVE_AI_ASSISTANT}(aiAssistantID: $aiAssistantID)
  }
`;

export const TRANSCRIBE_AI_VOICE_QUERY_MUTATION = gql`
  mutation ${TRANSCRIBE_AI_VOICE_QUERY}($input: InputTranscribeAiVoiceQuery!) {
    ${TRANSCRIBE_AI_VOICE_QUERY}(input: $input) {
      _id
      audioUrl
      languageCode
      transcript
      transcriptionStatus
    }
  }
`;

export const REMOVE_AI_ASSISTANT_DOCUMENTS_MUTATION = gql`
  mutation ${REMOVE_AI_ASSISTANT_DOCUMENTS}($input: InputRemoveAiAssistantDocuments!) {
    ${REMOVE_AI_ASSISTANT_DOCUMENTS}(input: $input)
  }
`;

export const RENAME_AI_ASSISTANT_MUTATION = gql`
  mutation ${RENAME_AI_ASSISTANT}($input: InputRenameAiAssistant!) {
    ${RENAME_AI_ASSISTANT}(input: $input)
  }
`;

export const SUBMIT_IDEA_SUGGESTION_MUTATION = gql`
  mutation ${SUBMIT_IDEA_SUGGESTION}($suggestion: String!) {
    ${SUBMIT_IDEA_SUGGESTION}(suggestion: $suggestion)
  }
`;

export const CREATE_OWN_OEM_SKILLS_MUTATION = gql`
  mutation createOwnOemSkills($input: InputCreateOwnOemSkills!) {
    createOwnOemSkills(input: $input) {
      skills {
        _id
        name
      }
    }
  }
`;

export const UPDATE_OWN_OEM_SKILL_MUTATION = gql`
  mutation updateOwnOemSkill($input: InputUpdateOwnOemSkill!) {
    updateOwnOemSkill(input: $input)
  }
`;

export const DELETE_OWN_OEM_SKILL_MUTATION = gql`
  mutation deleteOwnOemSkill($input: InputDeleteOwnOemSkill!) {
    deleteOwnOemSkill(input: $input)
  }
`;

export const CREATE_OEM_RESOURCE_MUTATION = gql`
  mutation createOwnOemResource($input: InputCreateOwnOemResource!) {
    createOwnOemResource(input: $input) {
      ...ResourceData
    }
  }
  ${Resource.fragments.ResourceData}
`;

export const UPDATE_OEM_RESOURCE_MUTATION = gql`
  mutation updateOwnOemResource($input: InputUpdateOwnOemResource!) {
    updateOwnOemResource(input: $input) {
      ...ResourceData
    }
  }
  ${Resource.fragments.ResourceData}
`;

export const DELETE_OEM_RESOURCE_MUTATION = gql`
  mutation deleteOwnOemResource($input: InputDeleteOwnOemResource!) {
    deleteOwnOemResource(input: $input)
  }
`;

export const ASSIGN_MULTIPLE_SKILLS_USER_MUTATION = gql`
  mutation assignMultipleSkillsToOemUser(
    $input: InputAssignUnassignMultipleSkillsOemUser!
  ) {
    assignMultipleSkillsToOemUser(input: $input) {
      _id
      skills {
        _id
      }
    }
  }
`;

export const UPDATE_TICKET_SCHEDULE_DRAFT = gql`
  mutation updateTicketScheduleDraft($input: InputUpdateTicketScheduleDraft!) {
    updateTicketScheduleDraft(input: $input) {
      ...TicketScheduleDraftData
    }
  }
  ${TicketScheduleDraft.fragments.TicketScheduleDraftData}
`;

export const PUBLISH_TICKET_SCHEDULE_DRAFT = gql`
  mutation publishTicketScheduleDraft(
    $input: InputPublishTicketScheduleDraft!
  ) {
    publishTicketScheduleDraft(input: $input) {
      ...TicketScheduleDraftData
    }
  }
  ${TicketScheduleDraft.fragments.TicketScheduleDraftData}
`;

export const LINK_CALENDAR_SYNC_ACCOUNT_MUTATION = gql`
  mutation linkCalendarSyncAccount($code: String!) {
    linkCalendarSyncAccount(code: $code) {
      calendarSyncAccountLinked
      calendarSyncAccountStatus
      linkedCalendarSyncEmailAddress
      calendarSyncAccountNeedsReconnect
    }
  }
`;

export const UNLINK_CALENDAR_SYNC_ACCOUNT_MUTATION = gql`
  mutation unlinkCalendarSyncAccount {
    unlinkCalendarSyncAccount {
      calendarSyncAccountLinked
      calendarSyncAccountStatus
      linkedCalendarSyncEmailAddress
      calendarSyncAccountNeedsReconnect
    }
  }
`;

export const CREATE_REQUEST_MUTATION = gql`
  mutation ${CREATE_REQUEST}($input: InputCreateRequest!) {
    ${CREATE_REQUEST}(input: $input) {
      ...TicketSingleData
    }
  }
  ${Ticket.fragments.TicketSingleData}
`;

export const DELETE_AI_NOTE_MUTATION = gql`
  mutation deleteAiNote($id: ID!) {
    deleteAiNote(id: $id)
  }
`;

export const ASSIGN_AI_NOTES_TO_TICKET_MUTATION = gql`
  mutation assignAiNotesToTicket($input: InputAssignAiNotesToTicket!) {
    assignAiNotesToTicket(input: $input) {
      ...AiNoteWorkOrderData
    }
  }
  ${AiNote.fragments.AiNoteWorkOrderData}
`;

export const PUBLISH_AI_NOTE_MUTATION = gql`
  mutation publishAiNote($input: InputPublishAiNote!) {
    publishAiNote(input: $input) {
      ...AiNoteData
    }
  }
  ${AiNote.fragments.AiNoteData}
`;

export const UNASSIGN_AI_NOTE_FROM_TICKET_MUTATION = gql`
  mutation unassignAiNoteFromTicket($input: InputUnassignAiNoteFromTicket!) {
    unassignAiNoteFromTicket(input: $input) {
      ...AiNoteWorkOrderData
    }
  }
  ${AiNote.fragments.AiNoteWorkOrderData}
`;

export const TRANSLATE_AI_NOTE_SUMMARY_MUTATION = gql`
  mutation translateAiNoteSummary($input: InputTranslateAiNoteSummary!) {
    translateAiNoteSummary(input: $input)
  }
`;

export const UPDATE_AI_NOTE_SUMMARY_MUTATION = gql`
  mutation updateAiNoteSummary($input: InputUpdateAiNoteSummary!) {
    updateAiNoteSummary(input: $input) {
      ...AiNoteData
    }
  }
  ${AiNote.fragments.AiNoteData}
`;

export const UPDATE_AI_NOTE_TITLE_MUTATION = gql`
  mutation updateAiNoteTitle($input: InputUpdateAiNoteTitle!) {
    updateAiNoteTitle(input: $input) {
      ...AiNoteData
    }
  }
  ${AiNote.fragments.AiNoteData}
`;

export const UPDATE_USER_EMAIL_SIGNATURE_MUTATION = gql`
  mutation updateEmailSignature(
    $signature: String!
    $autoAppendEmailSignature: Boolean!
  ) {
    updateEmailSignature(
      signature: $signature
      autoAppendEmailSignature: $autoAppendEmailSignature
    ) {
      emailSignature
      autoAppendEmailSignature
    }
  }
`;

export const UPDATE_DATE_TIME_FORMAT_MUTATION = gql`
  mutation updateDateTimeFormat($dateFormat: String!, $timeFormat: String!) {
    updateDateTimeFormat(dateFormat: $dateFormat, timeFormat: $timeFormat) {
      dateFormat
      timeFormat
    }
  }
`;

export const CREATE_OWN_OEM_ASSET_TYPE_MUTATION = gql`
  mutation createOwnOemAssetType($input: InputCreateOwnOemAssetType!) {
    createOwnOemAssetType(input: $input) {
      assetTypes {
        _id
        name
      }
    }
  }
`;

export const UPDATE_OWN_OEM_ASSET_TYPE_MUTATION = gql`
  mutation updateOwnOemAssetType($input: InputUpdateOwnOemAssetType!) {
    updateOwnOemAssetType(input: $input)
  }
`;

export const DELETE_OWN_OEM_ASSET_TYPE_MUTATION = gql`
  mutation deleteOwnOemAssetType($input: InputDeleteOwnOemAssetType!) {
    deleteOwnOemAssetType(input: $input)
  }
`;

export const ASSIGN_OWN_OEM_INVENTORY_PARTS_TO_ASSET_TEMPLATE_MUTATION = gql`
  mutation assignOwnOemInventoryPartsToAssetTemplate(
    $input: InputAssignAssetTemplateInventoryParts
  ) {
    assignOwnOemInventoryPartsToAssetTemplate(input: $input) {
      ...AssetTemplateFullData
    }
  }
  ${AssetTemplate.fragments.AssetTemplateFullData}
`;

export const DELETE_OWN_OEM_ASSET_TEMPLATE_MUTATION = gql`
  mutation deleteOwnOemAssetTemplate($templateId: ID!) {
    deleteOwnOemAssetTemplate(templateId: $templateId) {
      _id
    }
  }
`;

export const CREATE_OWN_OEM_ASSET_TEMPLATE_MUTATION = gql`
  mutation createOwnOemAssetTemplate($input: InputCreateAssetTemplate!) {
    createOwnOemAssetTemplate(input: $input) {
      ...AssetTemplateData
    }
  }
  ${AssetTemplate.fragments.AssetTemplateData}
`;

export const REMOVE_OWN_OEM_INVENTORY_PART_FROM_ASSET_TEMPLATE_MUTATION = gql`
  mutation removeOwnOemInventoryPartFromAssetTemplate(
    $input: InputRemoveAssetTemplateInventoryPart
  ) {
    removeOwnOemInventoryPartFromAssetTemplate(input: $input) {
      ...AssetTemplateFullData
    }
  }
  ${AssetTemplate.fragments.AssetTemplateFullData}
`;

export const UPDATE_OWN_OEM_ASSET_TEMPLATE_MUTATION = gql`
  mutation updateOwnOemAssetTemplate($input: InputUpdateAssetTemplate!) {
    updateOwnOemAssetTemplate(input: $input) {
      ...AssetTemplateData
    }
  }
  ${AssetTemplate.fragments.AssetTemplateData}
`;

export const ASSIGN_ASSETS_TO_PARENT_MUTATION = gql`
  mutation assignAssetsToParent($input: InputAssignAssetsToParent!) {
    assignAssetsToParent(input: $input) {
      ...AssetFullData
    }
  }
  ${Asset.fragments.AssetFullData}
`;

export const ASSIGN_MULTIPLE_ASSETS_TO_TEAM_MUTATION = gql`
  mutation assignMultipleAssetsToTeam(
    $input: InputAssignMultipleAssetsToTeam!
  ) {
    assignMultipleAssetsToTeam(input: $input) {
      _id
    }
  }
`;

export const ASSIGN_OWN_OEM_MULTIPLE_ASSETS_TO_OWN_OEM_CUSTOMER = gql`
  mutation assignOwnOemMultipleAssetsToOwnOemCustomer(
    $input: InputAssignOwnOemMultipleAssetsToOwnOemCustomer!
  ) {
    assignOwnOemMultipleAssetsToOwnOemCustomer(input: $input) {
      ...AssetData
    }
  }
  ${Asset.fragments.AssetData}
`;

export const ASSIGN_OWN_OEM_MULTIPLE_ASSETS_TO_OWN_OEM_CUSTOMER_PORTAL = gql`
  mutation assignOwnOemMultipleAssetsToOwnOemCustomerPortal(
    $input: InputAssignOwnOemMultipleAssetsToOwnOemCustomerPortal!
  ) {
    assignOwnOemMultipleAssetsToOwnOemCustomerPortal(input: $input)
  }
`;

export const ASSIGN_OWN_OEM_PARTS_TO_ASSET_MUTATION = gql`
  mutation assignOwnOemInventoryPartsToAsset(
    $input: InputAssignAssetInventoryParts
  ) {
    assignOwnOemInventoryPartsToAsset(input: $input) {
      ...AssetPartsData
    }
  }
  ${Asset.fragments.AssetPartsData}
`;

export const CREATE_OWN_OEM_ASSET_MUTATION = gql`
  mutation createOwnOemAsset($input: InputCreateOwnOemAsset!) {
    createOwnOemAsset(input: $input) {
      ...AssetData
    }
  }
  ${Asset.fragments.AssetData}
`;

export const DELETE_OEM_ASSET_MUTATION = gql`
  mutation deleteOwnOemAsset($assetId: ID!) {
    deleteOwnOemAsset(assetId: $assetId)
  }
`;

export const DELETE_OWN_OEM_ASSET_IMAGE = gql`
  mutation deleteOwnOemAssetImage($assetId: ID!) {
    deleteOwnOemAssetImage(assetId: $assetId) {
      ...AssetData
    }
  }
  ${Asset.fragments.AssetData}
`;

export const HANDLE_OWN_OEM_ASSET_QR_ACCESS_MUTATION = gql`
  mutation handleOwnOemAssetQRAccess($input: InputHandleOwnOemAssetQRAccess!) {
    handleOwnOemAssetQRAccess(input: $input) {
      ...AssetData
    }
  }
  ${Asset.fragments.AssetData}
`;

export const REMOVE_OWN_OEM_ASSET_FROM_OWN_OEM_CUSTOMER = gql`
  mutation removeOwnOemAssetFromOwnOemCustomer(
    $input: InputRemoveOwnOemAssetFromOwnOemCustomer!
  ) {
    removeOwnOemAssetFromOwnOemCustomer(input: $input) {
      ...AssetData
    }
  }
  ${Asset.fragments.AssetData}
`;

export const REMOVE_OWN_OEM_ASSET_FROM_OWN_OEM_CUSTOMER_PORTAL = gql`
  mutation removeOwnOemAssetFromOwnOemCustomerPortal(
    $input: InputRemoveOwnOemAssetFromOwnOemCustomerPortal!
  ) {
    removeOwnOemAssetFromOwnOemCustomerPortal(input: $input)
  }
`;

export const REMOVE_OWN_OEM_PART_FROM_ASSET_MUTATION = gql`
  mutation removeOwnOemInventoryPartFromAsset(
    $input: InputRemoveAssetInventoryPart
  ) {
    removeOwnOemInventoryPartFromAsset(input: $input) {
      ...AssetPartsData
    }
  }
  ${Asset.fragments.AssetPartsData}
`;

export const UNASSIGN_ASSET_FROM_PARENT_MUTATION = gql`
  mutation unassignAssetFromParent($input: InputUnassignAssetFromParent!) {
    unassignAssetFromParent(input: $input) {
      ...AssetFullData
    }
  }
  ${Asset.fragments.AssetFullData}
`;

export const UNASSIGN_ASSET_FROM_TEAM_MUTATION = gql`
  mutation unassignAssetFromTeam($input: InputUnassignAssetFromTeam!) {
    unassignAssetFromTeam(input: $input) {
      _id
    }
  }
`;

export const UPDATE_OWN_OEM_ASSET_MUTATION = gql`
  mutation updateOwnOemAsset($input: InputUpdateOwnOemAsset!) {
    updateOwnOemAsset(input: $input) {
      ...AssetFullData
    }
  }
  ${Asset.fragments.AssetFullData}
`;

export const ASSIGN_PROCEDURES_TO_ASSET_MUTATION = gql`
  mutation assignProceduresToAsset($input: InputAssignProceduresToAsset!) {
    assignProceduresToAsset(input: $input) {
      ...AssetFullData
    }
  }
  ${Asset.fragments.AssetFullData}
`;

export const UNASSIGN_PROCEDURE_FROM_ASSET_MUTATION = gql`
  mutation unassignProcedureFromAsset(
    $input: InputUnassignProcedureFromAsset!
  ) {
    unassignProcedureFromAsset(input: $input) {
      ...AssetFullData
    }
  }
  ${Asset.fragments.AssetFullData}
`;

export const CREATE_CONTACT_MUTATION = gql`
  mutation ${CREATE_CONTACT}($input: InputCreateContact!) {
    ${CREATE_CONTACT}(input: $input) {
      _id
      name
      email
      jobTitle
      landline
      phoneNumber
      oem
      user
      connection {
        _id
        name
      }
    }
  }
`;

export const CREATE_CUSTOMER_PORTAL_MUTATION = gql`
  mutation ${CREATE_CUSTOMER_PORTAL}($connectionId: ID!, $assignAssets: Boolean) {
    ${CREATE_CUSTOMER_PORTAL}(connectionId: $connectionId, assignAssets: $assignAssets) {
      _id
      name
      connection {
        name
      }
      oem
    }
  }
`;

export const INVITE_CONTACTS_MUTATION = gql`
  mutation ${INVITE_CONTACTS}($input: InputInviteContacts!) {
    ${INVITE_CONTACTS}(input: $input)
  }
`;

export const REMOVE_CONTACT_INVITE_MUTATION = gql`
  mutation ${REMOVE_CONTACT_INVITE}($id: ID!) {
    ${REMOVE_CONTACT_INVITE}(id: $id)
  }
`;

export const RESEND_CONTACT_INVITE_MUTATION = gql`
  mutation ${RESEND_CONTACT_INVITE}($id: ID!) {
    ${RESEND_CONTACT_INVITE}(id: $id)
  }
`;

export const UPDATE_CONTACT_MUTATION = gql`
  mutation ${UPDATE_CONTACT}($input: InputUpdateContact!) {
    ${UPDATE_CONTACT}(input: $input) {
      _id
      name
      email
      jobTitle
      landline
      phoneNumber
      oem
      user
      connection {
        _id
        name
      }
      customFields {
        fieldId {
          ...CustomAdditionFieldData
        }
        value
        values
      }
    }
  }
  ${CustomizeField.fragments.CustomAdditionFieldData}
`;

export const UPDATE_CUSTOMER_PORTAL_MUTATION = gql`
  mutation ${UPDATE_CUSTOMER_PORTAL}($input: InputUpdateCustomerPortal!) {
    ${UPDATE_CUSTOMER_PORTAL}(input: $input) {
      _id
      name
      oem
      connection {
        name
      }
      allowInvitingMembers
      allowRequestCreation
      allowSubPortalCreation
      # The OEM's own grant, which is what the settings toggle reads back. Not
      # implied by allowSubPortalCreation: a parent portal's grant on the same
      # portal keeps that one true while this one goes false.
      subPortalCreation
      canEnableSubPortalCreation
      ticketReroute
      childPortalAssetVisibility
      allowSupportTicketCreation
      assetAccess {
        documentation
        parts
        preventiveMaintenance
        qrCodes
        history
      }
      productAccess {
        allowCopying
        documentation
        parts
        procedures
      }
    }
  }
`;

export const ACCEPT_CONNECTION_REQUEST_MUTATION = gql`
  mutation ${ACCEPT_CONNECTION_REQUEST}($requestId: ID!, $portalToAttach: ID!) {
    ${ACCEPT_CONNECTION_REQUEST}(requestId: $requestId, portalToAttach: $portalToAttach)
  }
`;

export const DECLINE_CONNECTION_REQUEST_MUTATION = gql`
  mutation ${DECLINE_CONNECTION_REQUEST}($requestId: ID!) {
    ${DECLINE_CONNECTION_REQUEST}(requestId: $requestId)
  }
`;

export const DELETE_CONTACT_MUTATION = gql`
  mutation ${DELETE_CONTACT}($id: ID!) {
    ${DELETE_CONTACT}(id: $id)
  }
`;

export const DELETE_CUSTOMER_PORTAL_MUTATION = gql`
  mutation ${DELETE_CUSTOMER_PORTAL}($id: ID!) {
    ${DELETE_CUSTOMER_PORTAL}(id: $id)
  }
`;

export const DELETE_DOCUMENTS_MUTATION = gql`
  mutation ${DELETE_DOCUMENTS}($documentsToDelete: [String]) {
    ${DELETE_DOCUMENTS}(documentsToDelete: $documentsToDelete)
  }
`;

export const UPDATE_DOCUMENT_MUTATION = gql`
  mutation ${UPDATE_DOCUMENT}($input: InputUpdateDocument) {
    ${UPDATE_DOCUMENT}(input: $input) {
      ...DocumentData
    }
  }
  ${Document.fragments.DocumentData}
`;

export const SCAN_DOCUMENTS_MUTATION = gql`
  mutation ${SCAN_DOCUMENTS}($documentsToScan: [String]) {
    ${SCAN_DOCUMENTS}(documentsToScan: $documentsToScan)
  }
`;

export const INDEX_DOCUMENTS_MUTATION = gql`
  mutation ${INDEX_DOCUMENTS}($documentsToIndex: [String]) {
    ${INDEX_DOCUMENTS}(documentsToIndex: $documentsToIndex)
  }
`;

export const APPROVE_OCR_DOCUMENTS_MUTATION = gql`
  mutation ${APPROVE_OCR_DOCUMENTS}($documentsToApprove: [String]) {
    ${APPROVE_OCR_DOCUMENTS}(documentsToApprove: $documentsToApprove)
  }
`;

export const UPLOAD_SEARCH_FEEDBACK_MUTATION = gql`
  mutation ${UPLOAD_SEARCH_FEEDBACK}($input: InputUploadSearchFeedback) {
    ${UPLOAD_SEARCH_FEEDBACK}(input: $input)
  }
`;

export const VERIFY_SIGNUP_TOKEN_MUTATION = gql`
  mutation verifySignUpToken($token: String!) {
    verifySignUpToken(token: $token) {
      email
      userExists
      isInvitationExpired
      id
      userAdded
      emailMismatch
      notApprovedByOwner
      message
      isError
      isInvalidToken
    }
  }
`;

export const APPROVE_INVITE_MUTATION = gql`
  mutation approveInvite($token: String!) {
    approveInvite(token: $token)
  }
`;

export const DECLINE_INVITE_MUTATION = gql`
  mutation declineInvite($token: String!) {
    declineInvite(token: $token)
  }
`;

export const DETACH_OWN_OEM_ASSET_DOCUMENTATION = gql`
  mutation detachOwnOemAssetDocumentation($_id: ID!) {
    detachOwnOemAssetDocumentation(_id: $_id) {
      ...AssetFullData
    }
  }
  ${Asset.fragments.AssetFullData}
`;

export const RESET_OWN_OEM_ASSET_TO_TEMPLATE = gql`
  mutation resetOwnOemAssetToTemplate($input: InputResetAssetToTemplate!) {
    resetOwnOemAssetToTemplate(input: $input) {
      ...AssetFullData
      ...AssetPartsData
    }
  }
  ${Asset.fragments.AssetFullData}
  ${Asset.fragments.AssetPartsData}
`;

export const GET_GENERAL_SIGNUP_TOKEN_MUTATION = gql`
  mutation getGeneralSignUpToken($input: InputGetGeneralSignUpToken!) {
    getGeneralSignUpToken(input: $input)
  }
`;

export const VERIFY_GENERAL_SIGNUP_TOKEN_MUTATION = gql`
  mutation verifyGeneralSignUpToken($token: String!) {
    verifyGeneralSignUpToken(token: $token) {
      email
      redirectUrl
    }
  }
`;

export const CREATE_DOCUMENTS_MUTATION = gql`
  mutation ${CREATE_DOCUMENTS}($input: InputCreateDocuments) {
    ${CREATE_DOCUMENTS}(input: $input)
  }
`;

export const REMOVE_FIRST_SIGN_IN_REDIRECT_URL_MUTATION = gql`
  mutation removeFirstSignInRedirectUrl {
    removeFirstSignInRedirectUrl {
      ...UserAuthData
    }
  }
  ${User.fragments.UserAuthData}
`;

export const CREATE_AI_ASSISTANT_CHAT_MUTATION = gql`
  mutation createAiAssistantChat($aiAssistantId: ID!) {
    createAiAssistantChat(aiAssistantId: $aiAssistantId) {
      ...AiAssistantChatData
    }
  }
  ${AiAssistantChat.fragments.FullData}
`;

export const DELETE_AI_ASSISTANT_CHAT_MUTATION = gql`
  mutation deleteAiAssistantChat($aiAssistantChatId: ID!) {
    deleteAiAssistantChat(aiAssistantChatId: $aiAssistantChatId)
  }
`;

export const RENAME_AI_ASSISTANT_CHAT_MUTATION = gql`
  mutation renameAiAssistantChat($input: InputRenameAiAssistantChat!) {
    renameAiAssistantChat(input: $input)
  }
`;

export const UPDATE_AI_ASSISTANT_MUTATION = gql`
  mutation updateAiAssistant($input: InputUpdateAiAssistant!) {
    updateAiAssistant(input: $input)
  }
`;

export const UPDATE_DEFAULT_PROCEDURE_HEADER_MUTATION = gql`
  mutation updateDefaultProcedureHeader($header: InputFieldAttachment!) {
    updateDefaultProcedureHeader(header: $header) {
      ...OemBasicData
    }
  }
  ${Oem.fragments.OemBasicData}
`;

export const UPDATE_DEFAULT_PROCEDURE_FOOTER_MUTATION = gql`
  mutation updateDefaultProcedureFooter($footer: InputFieldAttachment!) {
    updateDefaultProcedureFooter(footer: $footer) {
      ...OemBasicData
    }
  }
  ${Oem.fragments.OemBasicData}
`;

export const REMOVE_DEFAULT_PROCEDURE_HEADER_MUTATION = gql`
  mutation removeDefaultProcedureHeader {
    removeDefaultProcedureHeader {
      ...OemBasicData
    }
  }
  ${Oem.fragments.OemBasicData}
`;

export const REMOVE_DEFAULT_PROCEDURE_FOOTER_MUTATION = gql`
  mutation removeDefaultProcedureFooter {
    removeDefaultProcedureFooter {
      ...OemBasicData
    }
  }
  ${Oem.fragments.OemBasicData}
`;

export const UPDATE_PROCEDURE_PREFERENCES_MUTATION = gql`
  mutation UpdateProcedurePreferences(
    $input: InputUpdateProcedurePreferences!
  ) {
    updateProcedurePreferences(input: $input) {
      ...OemBasicData
    }
  }
  ${Oem.fragments.OemBasicData}
`;

export const UPDATE_BUSINESS_HOURS_MUTATION = gql`
  mutation UpdateBusinessHours($input: InputUpdateBusinessHours!) {
    updateBusinessHours(input: $input) {
      ...OemBasicData
    }
  }
  ${Oem.fragments.OemBasicData}
`;

export const UPDATE_PERSONAL_WORKING_HOURS_MUTATION = gql`
  mutation UpdatePersonalWorkingHours(
    $input: InputUpdatePersonalWorkingHours!
  ) {
    updatePersonalWorkingHours(input: $input) {
      _id
      workingHours {
        dayOfWeek
        enabled
        from
        to
      }
      hasPersonalWorkingHours
    }
  }
`;

export const UPDATE_PROCEDURE_TEMPLATE_HEADER_MUTATION = gql`
  mutation updateProcedureTemplatePageHeader(
    $procedureId: ID!
    $header: InputFieldAttachment!
  ) {
    updateProcedureTemplatePageHeader(
      procedureId: $procedureId
      header: $header
    ) {
      ...ProcedureTemplateFullData
    }
  }
  ${ProcedureTemplate.fragments.ProcedureTemplateFullData}
`;

export const UPDATE_PROCEDURE_TEMPLATE_FOOTER_MUTATION = gql`
  mutation updateProcedureTemplatePageFooter(
    $procedureId: ID!
    $footer: InputFieldAttachment!
  ) {
    updateProcedureTemplatePageFooter(
      procedureId: $procedureId
      footer: $footer
    ) {
      ...ProcedureTemplateFullData
    }
  }
  ${ProcedureTemplate.fragments.ProcedureTemplateFullData}
`;

export const REMOVE_PROCEDURE_TEMPLATE_HEADER_MUTATION = gql`
  mutation removeProcedureTemplatePageHeader($procedureId: ID!) {
    removeProcedureTemplatePageHeader(procedureId: $procedureId) {
      ...ProcedureTemplateFullData
    }
  }
  ${ProcedureTemplate.fragments.ProcedureTemplateFullData}
`;

export const REMOVE_PROCEDURE_TEMPLATE_FOOTER_MUTATION = gql`
  mutation removeProcedureTemplatePageFooter($procedureId: ID!) {
    removeProcedureTemplatePageFooter(procedureId: $procedureId) {
      ...ProcedureTemplateFullData
    }
  }
  ${ProcedureTemplate.fragments.ProcedureTemplateFullData}
`;

export const CREATE_CONNECTION_REQUEST_MUTATION = gql`
  mutation CreateConnectionRequest($input: InputCreateConnectionRequest!) {
    createConnectionRequest(input: $input)
  }
`;

export const WORKSPACE_AUTHORIZATION_MUTATION = gql`
  mutation workspaceAuthorization($input: InputWorkspaceAuthorization!) {
    workspaceAuthorization(input: $input)
  }
`;

export const SAVE_FORM_CONFIG_MUTATION = gql`
  mutation saveFormConfig($input: InputSaveFormConfig!) {
    saveFormConfig(input: $input) {
      _id
      formKey
      defaultFields {
        fieldKey
        required
      }
      customFields {
        fieldId
        enabled
        required
      }
    }
  }
`;

export const ASSIGN_OWN_OEM_TICKET_COMPANY_MUTATION = gql`
  mutation AssignOwnOemTicketCompany($input: InputAssignOwnOemTicketCompany!) {
    assignOwnOemTicketCompany(input: $input) {
      _id
      facility {
        _id
        name
      }
    }
  }
`;

export const BULK_DELETE_OWN_OEM_ASSETS_MUTATION = gql`
  mutation bulkDeleteOwnOemAssets(
    $assetIds: [ID!]!
    $deleteSubAssets: Boolean
  ) {
    bulkDeleteOwnOemAssets(
      assetIds: $assetIds
      deleteSubAssets: $deleteSubAssets
    ) {
      succeeded
      failed {
        id
        reason
      }
    }
  }
`;

export const ASSIGN_OWN_OEM_TICKET_ASSETS_MUTATION = gql`
  mutation AssignOwnOemTicketAssets($input: InputAssignOwnOemTicketAssets!) {
    assignOwnOemTicketAssets(input: $input) {
      _id
      assets {
        _id
        name
        serialNumber
      }
      facility {
        _id
        name
      }
    }
  }
`;

export const BULK_DELETE_OWN_OEM_TICKETS_MUTATION = gql`
  mutation bulkDeleteOwnOemTickets(
    $ticketIds: [ID!]!
    $reason: String
    $deleteSubWorkOrders: Boolean
  ) {
    bulkDeleteOwnOemTickets(
      ticketIds: $ticketIds
      reason: $reason
      deleteSubWorkOrders: $deleteSubWorkOrders
    ) {
      succeeded
      failed {
        id
        reason
      }
    }
  }
`;

export const BULK_DELETE_OWN_OEM_INVENTORY_PARTS_MUTATION = gql`
  mutation bulkDeleteOwnOemInventoryParts($ids: [ID!]!) {
    bulkDeleteOwnOemInventoryParts(ids: $ids) {
      succeeded
      failed {
        id
        reason
      }
    }
  }
`;

export const BULK_DELETE_OWN_OEM_ASSET_TEMPLATES_MUTATION = gql`
  mutation bulkDeleteOwnOemAssetTemplates($templateIds: [ID!]!) {
    bulkDeleteOwnOemAssetTemplates(templateIds: $templateIds) {
      succeeded
      failed {
        id
        reason
      }
    }
  }
`;

export const BULK_DELETE_OEM_CUSTOMERS_MUTATION = gql`
  mutation bulkDeleteOemCustomers(
    $customerIds: [ID!]!
    $deleteSubCompanies: Boolean
  ) {
    bulkDeleteOemCustomers(
      customerIds: $customerIds
      deleteSubCompanies: $deleteSubCompanies
    ) {
      succeeded
      failed {
        id
        reason
      }
    }
  }
`;

export const BULK_DELETE_CUSTOMER_PORTALS_MUTATION = gql`
  mutation bulkDeleteCustomerPortals($ids: [ID!]!) {
    bulkDeleteCustomerPortals(ids: $ids) {
      succeeded
      failed {
        id
        reason
      }
    }
  }
`;

export const BULK_DELETE_CONTACTS_MUTATION = gql`
  mutation bulkDeleteContacts($ids: [ID!]!) {
    bulkDeleteContacts(ids: $ids) {
      succeeded
      failed {
        id
        reason
      }
    }
  }
`;

export const BULK_DELETE_OWN_OEM_PROCEDURE_TEMPLATES_MUTATION = gql`
  mutation bulkDeleteOwnOemProcedureTemplates($ids: [ID!]!) {
    bulkDeleteOwnOemProcedureTemplates(ids: $ids) {
      succeeded
      failed {
        id
        reason
      }
    }
  }
`;

const FINANCE_SETTINGS_FIELDS = gql`
  fragment FinanceSettingsFields on FinanceSettings {
    _id
    senderDetails {
      companyName
      streetAddress
      postalCode
      country
      city
    }
    paymentInformation {
      vatId
      taxNumber
      iban
      bicSwift
      bankName
      accountHolderName
      accountNumber
      routingNumber
    }
    taxes {
      _id
      name
      percentage
    }
    documentDefaults {
      paymentTerms
      quotationNumberPrefix
      invoiceNumberPrefix
      invoiceFooterNotes
      quotationFooterNotes
    }
    branding {
      headerImageUrl
      footerImageUrl
    }
    numbering {
      nextQuotationNumber
      nextInvoiceNumber
    }
  }
`;

export const SAVE_FINANCE_SETTINGS_MUTATION = gql`
  ${FINANCE_SETTINGS_FIELDS}
  mutation saveFinanceSettings($input: InputSaveFinanceSettings!) {
    saveFinanceSettings(input: $input) {
      ...FinanceSettingsFields
    }
  }
`;

export const CREATE_FINANCE_PACKAGE_TYPE_MUTATION = gql`
  mutation createFinancePackageType($input: InputCreateFinancePackageType!) {
    createFinancePackageType(input: $input) {
      _id
      name
    }
  }
`;

export const UPDATE_FINANCE_PACKAGE_TYPE_MUTATION = gql`
  mutation updateFinancePackageType($input: InputUpdateFinancePackageType!) {
    updateFinancePackageType(input: $input) {
      _id
      name
    }
  }
`;

export const DELETE_FINANCE_PACKAGE_TYPE_MUTATION = gql`
  mutation deleteFinancePackageType(
    $id: ID!
    $input: InputDeleteFinancePackageType
  ) {
    deleteFinancePackageType(id: $id, input: $input)
  }
`;

export const CREATE_FINANCE_PACKAGE_MUTATION = gql`
  ${FINANCE_PACKAGE_FIELDS}
  mutation createFinancePackage($input: InputCreateFinancePackage!) {
    createFinancePackage(input: $input) {
      ...FinancePackageFields
    }
  }
`;

export const UPDATE_FINANCE_PACKAGE_MUTATION = gql`
  ${FINANCE_PACKAGE_FIELDS}
  mutation updateFinancePackage($input: InputUpdateFinancePackage!) {
    updateFinancePackage(input: $input) {
      ...FinancePackageFields
    }
  }
`;

export const DELETE_FINANCE_PACKAGE_MUTATION = gql`
  mutation deleteFinancePackage($id: ID!) {
    deleteFinancePackage(id: $id)
  }
`;

export const CREATE_FINANCE_QUOTE_MUTATION = gql`
  mutation createFinanceQuote($input: InputCreateFinanceQuote!) {
    createFinanceQuote(input: $input) {
      _id
      quoteNumber
      status
      lineItems {
        _id
      }
    }
  }
`;

export const UPDATE_FINANCE_QUOTE_MUTATION = gql`
  mutation updateFinanceQuote($input: InputUpdateFinanceQuote!) {
    updateFinanceQuote(input: $input) {
      _id
      quoteNumber
      status
      totalAmount
      customerNotes
      internalNotes
      validUntilDate
      contactEmail
      contact {
        _id
        name
        email
      }
      lineItems {
        _id
        name
        quantity
        unit
        unitPrice
        discountPercent
        markupPercent
        total
        packageId
        packageName
      }
      taxes {
        _id
        taxId
        name
        percentage
      }
    }
  }
`;

export const SEND_FINANCE_QUOTE_MUTATION = gql`
  mutation sendFinanceQuote($input: InputSendFinanceQuote!) {
    sendFinanceQuote(input: $input) {
      _id
      status
      contactEmail
      contact {
        _id
        name
        email
      }
    }
  }
`;

export const DOWNLOAD_FINANCE_QUOTE_PDF_MUTATION = gql`
  mutation downloadFinanceQuotePDF($quoteId: ID!, $uuid: String!) {
    downloadFinanceQuotePDF(quoteId: $quoteId, uuid: $uuid)
  }
`;

export const ASSIGN_WORK_ORDER_TO_FINANCE_QUOTE_MUTATION = gql`
  mutation assignWorkOrderToFinanceQuote($quoteId: ID!, $workOrderId: ID!) {
    assignWorkOrderToFinanceQuote(
      quoteId: $quoteId
      workOrderId: $workOrderId
    ) {
      _id
      totalAmount
      workOrder {
        _id
        title
        ticketId
      }
      lineItems {
        _id
        name
        quantity
        unit
        unitPrice
        discountPercent
        markupPercent
        total
        packageId
        packageName
      }
    }
  }
`;

export const ATTACH_FINANCE_QUOTE_PURCHASE_ORDER_MUTATION = gql`
  mutation attachFinanceQuotePurchaseOrder(
    $quoteId: ID!
    $purchaseOrder: InputFinanceQuotePurchaseOrder!
  ) {
    attachFinanceQuotePurchaseOrder(
      quoteId: $quoteId
      purchaseOrder: $purchaseOrder
    ) {
      _id
      purchaseOrders {
        _id
        name
        url
        type
        size
      }
    }
  }
`;

export const REMOVE_FINANCE_QUOTE_PURCHASE_ORDER_MUTATION = gql`
  mutation removeFinanceQuotePurchaseOrder($quoteId: ID!, $url: String) {
    removeFinanceQuotePurchaseOrder(quoteId: $quoteId, url: $url) {
      _id
      purchaseOrders {
        _id
        name
        url
        type
        size
      }
    }
  }
`;

export const DUPLICATE_FINANCE_QUOTE_MUTATION = gql`
  mutation duplicateFinanceQuote($id: ID!) {
    duplicateFinanceQuote(id: $id) {
      _id
      quoteNumber
    }
  }
`;

export const DELETE_FINANCE_QUOTE_MUTATION = gql`
  mutation deleteFinanceQuote($id: ID!) {
    deleteFinanceQuote(id: $id)
  }
`;

export const CONVERT_FINANCE_QUOTE_TO_INVOICE_MUTATION = gql`
  mutation convertFinanceQuoteToInvoice($quoteId: ID!) {
    convertFinanceQuoteToInvoice(quoteId: $quoteId) {
      _id
      invoiceNumber
      status
      totalAmount
    }
  }
`;

export const CREATE_FINANCE_INVOICE_MUTATION = gql`
  mutation createFinanceInvoice($input: InputCreateFinanceInvoice!) {
    createFinanceInvoice(input: $input) {
      _id
      invoiceNumber
      status
      lineItems {
        _id
      }
    }
  }
`;

export const UPDATE_FINANCE_INVOICE_MUTATION = gql`
  mutation updateFinanceInvoice($input: InputUpdateFinanceInvoice!) {
    updateFinanceInvoice(input: $input) {
      _id
      invoiceNumber
      status
      totalAmount
      customerNotes
      internalNotes
      dueDate
      issueDate
      contactEmail
      contact {
        _id
        name
        email
      }
      lineItems {
        _id
        name
        quantity
        unit
        unitPrice
        discountPercent
        markupPercent
        total
        packageId
        packageName
      }
      taxes {
        _id
        taxId
        name
        percentage
      }
    }
  }
`;

export const SEND_FINANCE_INVOICE_MUTATION = gql`
  mutation sendFinanceInvoice($input: InputSendFinanceInvoice!) {
    sendFinanceInvoice(input: $input) {
      _id
      status
      contactEmail
      contact {
        _id
        name
        email
      }
    }
  }
`;

export const DOWNLOAD_FINANCE_INVOICE_PDF_MUTATION = gql`
  mutation downloadFinanceInvoicePDF($invoiceId: ID!, $uuid: String!) {
    downloadFinanceInvoicePDF(invoiceId: $invoiceId, uuid: $uuid)
  }
`;

export const ASSIGN_WORK_ORDER_TO_FINANCE_INVOICE_MUTATION = gql`
  mutation assignWorkOrderToFinanceInvoice($invoiceId: ID!, $workOrderId: ID!) {
    assignWorkOrderToFinanceInvoice(
      invoiceId: $invoiceId
      workOrderId: $workOrderId
    ) {
      _id
      totalAmount
      workOrder {
        _id
        title
        ticketId
      }
      lineItems {
        _id
        name
        quantity
        unit
        unitPrice
        discountPercent
        markupPercent
        total
        packageId
        packageName
      }
    }
  }
`;

export const ATTACH_FINANCE_INVOICE_PURCHASE_ORDER_MUTATION = gql`
  mutation attachFinanceInvoicePurchaseOrder(
    $invoiceId: ID!
    $purchaseOrder: InputFinanceInvoicePurchaseOrder!
  ) {
    attachFinanceInvoicePurchaseOrder(
      invoiceId: $invoiceId
      purchaseOrder: $purchaseOrder
    ) {
      _id
      purchaseOrders {
        _id
        name
        url
        type
        size
      }
    }
  }
`;

export const REMOVE_FINANCE_INVOICE_PURCHASE_ORDER_MUTATION = gql`
  mutation removeFinanceInvoicePurchaseOrder($invoiceId: ID!, $url: String) {
    removeFinanceInvoicePurchaseOrder(invoiceId: $invoiceId, url: $url) {
      _id
      purchaseOrders {
        _id
        name
        url
        type
        size
      }
    }
  }
`;

export const DUPLICATE_FINANCE_INVOICE_MUTATION = gql`
  mutation duplicateFinanceInvoice($id: ID!) {
    duplicateFinanceInvoice(id: $id) {
      _id
      invoiceNumber
    }
  }
`;

export const DELETE_FINANCE_INVOICE_MUTATION = gql`
  mutation deleteFinanceInvoice($id: ID!) {
    deleteFinanceInvoice(id: $id)
  }
`;

export const DOWNLOAD_PUBLIC_FINANCE_QUOTE_PDF_MUTATION = gql`
  mutation downloadPublicFinanceQuotePDF($oemSlug: String!, $token: String!) {
    downloadPublicFinanceQuotePDF(oemSlug: $oemSlug, token: $token)
  }
`;

export const REQUEST_PUBLIC_FINANCE_QUOTE_CHANGES_MUTATION = gql`
  mutation requestPublicFinanceQuoteChanges(
    $input: InputRequestPublicFinanceQuoteChanges!
  ) {
    requestPublicFinanceQuoteChanges(input: $input) {
      _id
      status
      quoteNumber
    }
  }
`;

export const APPROVE_PUBLIC_FINANCE_QUOTE_MUTATION = gql`
  mutation approvePublicFinanceQuote($input: InputApprovePublicFinanceQuote!) {
    approvePublicFinanceQuote(input: $input) {
      _id
      status
      quoteNumber
    }
  }
`;

export const DOWNLOAD_PUBLIC_FINANCE_INVOICE_PDF_MUTATION = gql`
  mutation downloadPublicFinanceInvoicePDF($oemSlug: String!, $token: String!) {
    downloadPublicFinanceInvoicePDF(oemSlug: $oemSlug, token: $token)
  }
`;

export const REQUEST_PUBLIC_FINANCE_INVOICE_CHANGES_MUTATION = gql`
  mutation requestPublicFinanceInvoiceChanges(
    $input: InputRequestPublicFinanceInvoiceChanges!
  ) {
    requestPublicFinanceInvoiceChanges(input: $input) {
      _id
      status
      invoiceNumber
    }
  }
`;

export const CREATE_AUTOMATION_MUTATION = gql`
  ${AUTOMATION_FIELDS}
  mutation createAutomation($input: InputCreateAutomation!) {
    createAutomation(input: $input) {
      ...AutomationFields
    }
  }
`;

export const UPDATE_AUTOMATION_MUTATION = gql`
  ${AUTOMATION_FIELDS}
  mutation updateAutomation($input: InputUpdateAutomation!) {
    updateAutomation(input: $input) {
      ...AutomationFields
    }
  }
`;

export const SET_AUTOMATION_ACTIVE_MUTATION = gql`
  ${AUTOMATION_FIELDS}
  mutation setAutomationActive($id: ID!, $active: Boolean!) {
    setAutomationActive(id: $id, active: $active) {
      ...AutomationFields
    }
  }
`;

export const DELETE_AUTOMATION_MUTATION = gql`
  ${AUTOMATION_FIELDS}
  mutation deleteAutomation($id: ID!) {
    deleteAutomation(id: $id) {
      ...AutomationFields
    }
  }
`;

export const SUGGEST_AUTOMATION_TYPE_MUTATION = gql`
  mutation suggestAutomationType($input: InputSuggestAutomationType!) {
    suggestAutomationType(input: $input)
  }
`;
